import axios from "axios";

// If VITE_API_BASE_URL is set, use it. Otherwise use relative URL (for Vite proxy)
const API_URL = import.meta.env.VITE_API_BASE_URL || "";

let cachedToken: string | null = null;
let tokenExpiry: number | null = null;
let fetchPromise: Promise<string> | null = null;

/**
 * Fetch a fresh CSRF token from the API
 */
export async function fetchCSRFToken(): Promise<string> {
  // If there's already a fetch in progress, return that promise
  if (fetchPromise) {
    return fetchPromise;
  }

  fetchPromise = (async () => {
    try {
      const response = await axios.get(`${API_URL}/api/v1/csrf-token`, {
        timeout: 10000,
        headers: {
          Accept: "application/json",
        },
      });

      const token = response.data.csrfToken;

      if (!token) {
        throw new Error("CSRF token not found in response");
      }

      cachedToken = token;
      // Cache for 55 minutes (tokens expire after 1 hour)
      tokenExpiry = Date.now() + 55 * 60 * 1000;

      return token;
    } catch (error) {
      console.error("[CSRF] Failed to fetch token:", error);
      throw error;
    } finally {
      fetchPromise = null;
    }
  })();

  return fetchPromise;
}

/**
 * Get a valid CSRF token (from cache or fetch new one)
 */
export async function getCSRFToken(): Promise<string> {
  // Return cached token if still valid
  if (cachedToken && tokenExpiry && Date.now() < tokenExpiry) {
    return cachedToken;
  }

  // Clear expired cache
  cachedToken = null;
  tokenExpiry = null;

  return await fetchCSRFToken();
}

/**
 * Clear the cached CSRF token
 */
export function clearCSRFToken(): void {
  cachedToken = null;
  tokenExpiry = null;
  fetchPromise = null;
}

/**
 * Initialize CSRF token on app startup
 * Call this early to pre-fetch the token
 */
export async function initCSRFToken(): Promise<void> {
  try {
    await fetchCSRFToken();
  } catch (error) {
    console.warn("[CSRF] Failed to pre-fetch token:", error);
    // Don't throw - let individual requests handle the error
  }
}
